import { useNavigate } from "react-router-dom";
import { MoreHorizontal, Eye, Pencil, Copy, Ban, Trash2, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuSub,
  DropdownMenuSubContent, DropdownMenuSubTrigger, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { StatusBadge, JobTypeChip } from "@/components/common";
import { STATUSES } from "@/lib/constants";
import { fmtDate, fmtTime, money } from "@/lib/format";
import { cn } from "@/lib/utils";

const RowMenu = ({ b, actions }) => {
  const navigate = useNavigate();
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
        <Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`row-menu-${b.id}`}>
          <MoreHorizontal className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()} className="w-48">
        <DropdownMenuItem onClick={() => navigate(`/bookings/${b.id}`)} data-testid={`row-view-${b.id}`}><Eye className="h-4 w-4" />Open job</DropdownMenuItem>
        <DropdownMenuItem onClick={() => actions.edit(b)} data-testid={`row-edit-${b.id}`}><Pencil className="h-4 w-4" />Edit</DropdownMenuItem>
        <DropdownMenuItem onClick={() => actions.duplicate(b)} data-testid={`row-duplicate-${b.id}`}><Copy className="h-4 w-4" />Duplicate</DropdownMenuItem>
        <DropdownMenuSub>
          <DropdownMenuSubTrigger data-testid={`row-status-${b.id}`}>Change status</DropdownMenuSubTrigger>
          <DropdownMenuSubContent>
            {STATUSES.map((s) => (
              <DropdownMenuItem key={s} onClick={() => actions.setStatus(b, s)} data-testid={`row-status-${b.id}-${s}`}>
                <StatusBadge status={s} />
              </DropdownMenuItem>
            ))}
          </DropdownMenuSubContent>
        </DropdownMenuSub>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => actions.cancel(b)} data-testid={`row-cancel-${b.id}`}><Ban className="h-4 w-4" />Cancel booking</DropdownMenuItem>
        <DropdownMenuItem onClick={() => actions.remove(b)} className="text-rose-600 focus:text-rose-700" data-testid={`row-delete-${b.id}`}><Trash2 className="h-4 w-4" />Delete</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

const COLS = ["Date", "Move Pro #", "Customer", "Pickup", "Delivery", "Start", "Job type", "Men", "Truck", "Price", "Deposit", "Balance", "Team", "Status", ""];

export const BookingTable = ({ bookings, actions, emptyText = "No bookings found" }) => {
  const navigate = useNavigate();
  if (!bookings.length)
    return <div className="rounded-xl border border-dashed border-slate-300 bg-white py-16 text-center text-sm text-slate-500" data-testid="bookings-empty">{emptyText}</div>;
  return (
    <>
      <div className="hidden overflow-x-auto rounded-xl border border-slate-200 bg-white md:block">
        <table className="w-full text-sm" data-testid="bookings-table">
          <thead>
            <tr className="border-b border-slate-200 bg-slate-50 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500">
              {COLS.map((c) => <th key={c} className="whitespace-nowrap px-3 py-3">{c}</th>)}
            </tr>
          </thead>
          <tbody>
            {bookings.map((b) => (
              <tr key={b.id} onClick={() => navigate(`/bookings/${b.id}`)} data-testid={`booking-row-${b.id}`}
                className={cn("cursor-pointer border-b border-slate-100 transition-colors last:border-0 hover:bg-sky-50/60", ["Cancelled", "Refunded"].includes(b.status) && "opacity-60")}>
                <td className="whitespace-nowrap px-3 py-3 font-medium text-slate-800">{fmtDate(b.moving_date, "EEE d MMM")}</td>
                <td className="px-3 py-3 font-mono font-semibold text-[#0E1E38]" data-testid={`row-job-number-${b.id}`}>{b.job_number}</td>
                <td className="px-3 py-3"><div className="flex items-center gap-1.5 font-medium text-slate-900">{b.customer_name}{b.missing_info?.length > 0 && <AlertTriangle className="h-3.5 w-3.5 shrink-0 text-amber-500" title={`Missing: ${b.missing_info.join(", ")}`} data-testid={`flag-${b.id}`} />}</div><div className="text-xs text-slate-500">{b.mobile}</div></td>
                <td className="max-w-[160px] truncate px-3 py-3 text-slate-600" title={b.pickup_address}>{b.pickup_address}</td>
                <td className="max-w-[160px] truncate px-3 py-3 text-slate-600" title={b.delivery_address}>{b.delivery_address}</td>
                <td className="whitespace-nowrap px-3 py-3 font-mono text-xs">{fmtTime(b.start_time)}</td>
                <td className="whitespace-nowrap px-3 py-3"><JobTypeChip name={b.job_type} /></td>
                <td className="px-3 py-3 text-center">{b.men || "—"}</td>
                <td className="whitespace-nowrap px-3 py-3 text-slate-600">{b.truck_assigned || b.truck_size || "—"}</td>
                <td className="whitespace-nowrap px-3 py-3 font-mono text-xs font-semibold">{money(b.total_estimate)}</td>
                <td className="whitespace-nowrap px-3 py-3 font-mono text-xs">{b.deposit_amount ? <span className="text-emerald-700">{money(b.deposit_amount)}</span> : <span className="text-slate-400">—</span>}</td>
                <td className="whitespace-nowrap px-3 py-3 font-mono text-xs font-semibold text-amber-800">{money(b.remaining_balance)}</td>
                <td className="whitespace-nowrap px-3 py-3 text-slate-600">{b.assigned_team || <span className="text-amber-600">Unassigned</span>}</td>
                <td className="px-3 py-3"><StatusBadge status={b.status} testId={`status-${b.id}`} /></td>
                <td className="px-2 py-3"><RowMenu b={b} actions={actions} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="space-y-3 md:hidden">
        {bookings.map((b) => (
          <div key={b.id} onClick={() => navigate(`/bookings/${b.id}`)} data-testid={`booking-card-${b.id}`} className="rounded-xl border border-slate-200 bg-white p-4">
            <div className="flex items-start justify-between gap-2">
              <div>
                <div className="font-mono text-xs font-semibold text-sky-700">{b.job_number} · {fmtDate(b.moving_date, "EEE d MMM")} · {fmtTime(b.start_time)}</div>
                <div className="mt-1 font-semibold text-slate-900">{b.customer_name}</div>
              </div>
              <div className="flex items-center gap-1"><StatusBadge status={b.status} /><RowMenu b={b} actions={actions} /></div>
            </div>
            <div className="mt-2 space-y-0.5 text-xs text-slate-600">
              <div className="truncate"><span className="text-slate-400">From</span> {b.pickup_address}</div>
              <div className="truncate"><span className="text-slate-400">To</span> {b.delivery_address}</div>
            </div>
            <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-500">
              <JobTypeChip name={b.job_type} /><span>{b.assigned_team || "Unassigned"}</span><span>Price {money(b.total_estimate)}</span><span>Bal {money(b.remaining_balance)}</span>
            </div>
          </div>
        ))}
      </div>
    </>
  );
};
